import React, { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router';
import classnames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Hooks
import { useResponsiveScreenWidth } from 'Wpds/hooks/useResponsiveScreenWidth';
import { useWorkspace } from 'Shared/utils/hooks/use-workspace';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useOrganization } from 'Shared/utils/hooks/use-organization';

// Utils
import PageUrlsUtil from 'Shared/utils/PageUrls.utils';

// Components
import { WpPageTitle } from 'Wpds/components/page-layout';
import { WpButtonGroup, WpButton } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';
import WorkspacePresenceUsers from './WorkspacePresenceUsers';
import WorkspacePersonalAvatar from './WorkspacePersonalAvatar';
import WorkspaceOneOnOneAvatars from './WorkspaceOneOnOneAvatars';
import WorkspacePageTitleActionsDropdown from './WorkspacePageTitleActionsDropdown';
import WorkspaceInviteButton from './WorkspaceInviteButton';
import WorkspacePageTitleForPI from './WorkspacePageTitleForPI';

// Constants
import ColorConstants from 'Wpds/colors/ColorConstants';

// Styles
import './WorkspacePageTitle.scss';

type Props = {
  workspaceId: string;
  toggleSidebar: () => void;
  isSideRailOpen: boolean;
  toggleIsSideRailOpen: () => void;
};

const WorkspacePageTitle = ({ workspaceId, toggleSidebar, isSideRailOpen, toggleIsSideRailOpen }: Props) => {
  const history = useHistory();
  const location = useLocation();
  const { isMedium } = useResponsiveScreenWidth({ isAtLeast: true });
  const { featureFlagShowBaInfoInUserProfile: ffShowBaInfo, enableDesignInWorkspaces } = useFlags();
  const { isPiUser } = useCurrentUser();
  const { isPiDemoAccount } = useOrganization();

  const { workspace, workspaceTitle, isPersonal, isGroup, isOneOnOne, isTeamSync, users } = useWorkspace();

  const showPiTitle = isPiUser && ffShowBaInfo && isMedium && (isPersonal || isOneOnOne);

  const baseClassName = 'WorkspacePageTitle';

  const getWorkspaceAvatars = () => {
    if (isGroup) return null;
    if (isPersonal) return <WorkspacePersonalAvatar />;
    if (isOneOnOne) return <WorkspaceOneOnOneAvatars />;
    return null;
  };

  const getWorkspaceDescription = () => {
    if (!isMedium) return null;
    if (isPersonal) return 'Your private space';
    if (isTeamSync)
      return 'A workspace for the whole team to collaborate. New members will always be added to this workspace.';
    if (isGroup) return workspace.description;
    return null;
  };

  const getTitlePostfix = () => {
    if (isPersonal) return null;
    if (isOneOnOne) {
      if (isMedium) return <WorkspaceInviteButton />;
      else
        return (
          <div className={`${baseClassName}__invite-btn`}>
            <WorkspaceInviteButton />
          </div>
        );
    }
    return null;
  };

  const handleMembersClick = useCallback(() => {
    history.push({
      pathname: PageUrlsUtil.makeWorkspaceSettingsUrl(workspaceId),
      state: { isModal: true, previousLocation: location, view: 'members' },
    });
  }, [location]);

  const showOpenSideRailButton =
    isGroup &&
    users.length >= 3 &&
    users.length <= 25 &&
    enableDesignInWorkspaces &&
    !isSideRailOpen &&
    (isPiDemoAccount || workspace.initialTemplateSelectionMade);

  const presenceClasses = classnames(
    baseClassName,
    `${baseClassName}__actions__presence`,
    isGroup && `${baseClassName}__actions__presence--isGroup`
  );

  return (
    <>
      <WpPageTitle
        title={workspaceTitle}
        subTitle={getWorkspaceDescription()}
        prefix={getWorkspaceAvatars()}
        postfix={isMedium && getTitlePostfix()}
        buttonsOnMobile="inline"
        buttonsContainer={
          <div className={`${baseClassName}__actions__container`}>
            <div className={`${baseClassName}__actions`}>
              {!isPersonal && isMedium && (
                <div
                  className={presenceClasses}
                  onClick={isGroup ? handleMembersClick : undefined}
                  data-test="workspace-page-members-wrapper"
                >
                  <WorkspacePresenceUsers />
                </div>
              )}
              <WpButtonGroup spacing="sm">
                {!isPersonal && (
                  <WpButton variant="tertiary" size="sm" onClick={toggleSidebar}>
                    <WpIcon name="clock" />
                  </WpButton>
                )}
              </WpButtonGroup>
              <WorkspacePageTitleActionsDropdown workspaceId={workspaceId} />
            </div>
            {showOpenSideRailButton && (
              <div className={`${baseClassName}__team-members`}>
                <WpButton onClick={toggleIsSideRailOpen} size="sm" variant="tertiary">
                  <WpIcon name="lightbulb-on" size="lg" color={ColorConstants.blue[500]} />{' '}
                  {isMedium ? 'Team members & tips' : 'Team & tips'}
                  <WpIcon name="long-arrow-right" />
                </WpButton>
              </div>
            )}
          </div>
        }
        customContentComponent={showPiTitle && <WorkspacePageTitleForPI />}
      />
      {!isMedium && getTitlePostfix()}
    </>
  );
};

export default WorkspacePageTitle;
